import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@eriko/ui/src/components/ui/sheet';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Separator } from '@eriko/ui/src/components/ui/separator';
import { Textarea } from '@eriko/ui/src/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import {
  useMatchDetail,
  useConfirmMatch,
  useRejectMatch,
} from '../../hooks/useReconciliation';
import { useReconciliationStore } from '../../stores/reconciliation';
import { TransferPairCard } from './TransferPairCard';
import type { ReconciliationMatchDTO, ResolvedPartyEntitySummary } from '@eriko/shared-types';
import { formatCurrency } from '@eriko/shared-types';
import {
  fetchPendingWritebackForEntity,
  retryWriteback,
  type PendingWritebackDTO,
} from '../../api/accounting';

interface Props {
  orgId: string;
}

export function MatchDetailDrawer({ orgId }: Props) {
  const matchId = useReconciliationStore((s) => s.drawerMatchId);
  const closeDrawer = useReconciliationStore((s) => s.closeDrawer);
  const openCreateRule = useReconciliationStore((s) => s.openCreateRule);

  const { data: match, isLoading, error } = useMatchDetail(orgId, matchId);
  const confirmMatch = useConfirmMatch(orgId);
  const rejectMatch = useRejectMatch(orgId);

  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const open = matchId !== null;

  const handleConfirm = async () => {
    if (!matchId) return;
    await confirmMatch.mutateAsync({ matchId });
    closeDrawer();
  };

  const handleReject = async () => {
    if (!matchId || !rejectReason.trim()) return;
    await rejectMatch.mutateAsync({ matchId, reason: rejectReason });
    setRejectMode(false);
    setRejectReason('');
    closeDrawer();
  };

  const handleAlwaysDoThis = () => {
    if (!match) return;
    const patternText = match.bankTransaction.merchantName ?? match.bankTransaction.description;
    openCreateRule({ patternText });
    closeDrawer();
  };

  const showActions =
    match && match.status !== 'CONFIRMED' && match.status !== 'REJECTED';

  return (
    <Sheet open={open} onOpenChange={(o) => !o && closeDrawer()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Match details</SheetTitle>
          <SheetDescription>
            Review reconciliation candidate and take action.
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-surface-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="py-12 text-center text-destructive">
            Failed to load match.
          </div>
        ) : !match ? null : match.matchedObject?.type === 'TRANSFER' ? (
          <TransferPairCard match={match} />
        ) : (
          <MatchDetailBody match={match} orgId={orgId} />
        )}

        {showActions && !rejectMode && (
          <SheetFooter className="mt-6 gap-2">
            <Button variant="outline" onClick={() => setRejectMode(true)}>Reject</Button>
            <Button variant="outline" onClick={handleAlwaysDoThis}>Always do this</Button>
            <Button onClick={handleConfirm} disabled={confirmMatch.isPending}>
              {confirmMatch.isPending ? 'Confirming…' : 'Confirm'}
            </Button>
          </SheetFooter>
        )}

        {showActions && rejectMode && (
          <div className="mt-6 space-y-3">
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why is this match wrong? (e.g. amount mismatch, wrong vendor)"
              className="min-h-[80px]"
              maxLength={500}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setRejectMode(false);
                  setRejectReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={!rejectReason.trim() || rejectMatch.isPending}
              >
                {rejectMatch.isPending ? 'Rejecting…' : 'Reject match'}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── ResolvedPartyChip ─────────────────────────────────────────────────────────
// Shows the sync status of a resolved AccountingEntity. Only rendered for
// DOCUMENT-type matches where document.resolvedPartyEntity is present.

function ResolvedPartyChip({ entity, orgId }: { entity: ResolvedPartyEntitySummary; orgId: string }) {
  const qc = useQueryClient();

  const { data: writeback } = useQuery<PendingWritebackDTO | null>({
    queryKey: ['admin', 'writeback', orgId, entity.id],
    queryFn: () => fetchPendingWritebackForEntity(entity.id, orgId),
    // Poll while PENDING or RUNNING so the chip updates without a page refresh.
    refetchInterval: (query) => {
      const wb = query.state.data;
      if (wb && (wb.status === 'PENDING' || wb.status === 'RUNNING')) return 5000;
      return false;
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => retryWriteback(id, orgId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'writeback', orgId, entity.id] });
    },
  });

  const provider = entity.provider?.toUpperCase();

  // QBO / Xero entity — already synced, no writeback row needed.
  if (provider === 'QBO' || provider === 'XERO') {
    const label = provider === 'QBO' ? 'QBO' : 'Xero';
    return (
      <Badge variant="default" className="text-[10px]">
        {label}
      </Badge>
    );
  }

  // LOCAL entity — show writeback status.
  if (!writeback || writeback.status === 'DONE') {
    return (
      <Badge variant="secondary" className="text-[10px]">
        LOCAL
      </Badge>
    );
  }

  if (writeback.status === 'PENDING' || writeback.status === 'RUNNING') {
    return (
      <Badge
        variant="outline"
        className="text-[10px] border-amber-300 text-amber-700 bg-amber-50"
      >
        LOCAL · sync pending
      </Badge>
    );
  }

  if (writeback.status === 'FAILED') {
    return (
      <span className="flex items-center gap-1.5">
        <Badge
          variant="destructive"
          className="text-[10px]"
          title={writeback.lastError ?? undefined}
        >
          LOCAL · sync failed
        </Badge>
        <Button
          size="sm"
          variant="outline"
          className="h-5 px-1.5 text-[10px]"
          disabled={retryMutation.isPending}
          onClick={() => retryMutation.mutate(writeback.id)}
        >
          {retryMutation.isPending ? '…' : 'Retry'}
        </Button>
      </span>
    );
  }

  return null;
}

// ── MatchDetailBody ───────────────────────────────────────────────────────────

function MatchDetailBody({ match, orgId }: { match: ReconciliationMatchDTO; orgId: string }) {
  const tx = match.bankTransaction;
  const isCredit = tx.type === 'CREDIT';
  const amount = `${isCredit ? '+' : '-'}${formatCurrency(
    Math.abs(parseFloat(tx.amount)),
    2,
    tx.currency ?? undefined,
  )}`;

  const isDocMatch =
    match.matchedObject?.type === 'RECEIPT' ||
    match.matchedObject?.type === 'EXPENSE' ||
    match.matchedObject?.type === 'PAYROLL' ||
    match.matchedObject?.type === 'TAX_PAYMENT';

  const docResolvedParty =
    isDocMatch && match.matchedObject && 'document' in match.matchedObject
      ? match.matchedObject.document.resolvedPartyEntity
      : null;

  return (
    <div className="mt-6 space-y-6">
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-2">
          Bank transaction
        </h4>
        <div className="rounded-md border border-surface-200 p-3 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="font-medium text-surface-900">
              {tx.description || tx.rawDescription || '(no description)'}
            </span>
            <span className={`font-mono ${isCredit ? 'text-emerald-700' : 'text-surface-900'}`}>
              {amount}
            </span>
          </div>
          <div className="text-xs text-surface-500">
            {new Date(tx.date).toLocaleDateString()}
            {tx.merchantName ? ` • ${tx.merchantName}` : ''}
          </div>
          {tx.rawDescription && tx.rawDescription !== tx.description && (
            <div className="text-xs text-surface-400 font-mono">{tx.rawDescription}</div>
          )}
        </div>
      </section>

      {match.matchedObject && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-2 flex items-center gap-2">
            Matched object
            <Badge variant="outline">{match.matchedObject.type}</Badge>
          </h4>
          <div className="rounded-md border border-surface-200 p-3 text-sm">
            {match.matchedObject.type === 'BILL' ||
            match.matchedObject.type === 'INVOICE' ||
            match.matchedObject.type === 'PAYOUT' ? (
              <>
                <div className="font-medium text-surface-900">
                  {match.matchedObject.entity.displayName}
                </div>
                <div className="text-xs text-surface-500">
                  {match.matchedObject.entity.externalId}
                  {match.matchedObject.entity.amount
                    ? ` • $${match.matchedObject.entity.amount}`
                    : ''}
                </div>
              </>
            ) : match.matchedObject.type === 'RECEIPT' ||
              match.matchedObject.type === 'EXPENSE' ||
              match.matchedObject.type === 'PAYROLL' ||
              match.matchedObject.type === 'TAX_PAYMENT' ? (
              <>
                <div className="font-medium text-surface-900">
                  {match.matchedObject.document.fileName}
                </div>
                <div className="text-xs text-surface-500">
                  {match.matchedObject.document.vendorName ??
                    match.matchedObject.document.customerName ??
                    '—'}
                  {match.matchedObject.document.totalAmount
                    ? ` • $${match.matchedObject.document.totalAmount}`
                    : ''}
                </div>
              </>
            ) : (
              <div className="text-surface-500 text-sm">Unknown match target</div>
            )}
          </div>
        </section>
      )}

      {/* Resolved party section — only for DOCUMENT matches with a resolved entity */}
      {isDocMatch && docResolvedParty && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-2">
            Resolved party
          </h4>
          <div className="rounded-md border border-surface-200 p-3 text-sm flex items-center justify-between gap-2">
            <span className="text-surface-900 font-medium">
              {docResolvedParty.displayName ?? '(unknown)'}
            </span>
            <ResolvedPartyChip entity={docResolvedParty} orgId={orgId} />
          </div>
        </section>
      )}

      {match.allocations && match.allocations.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-surface-500 mb-2">
            Allocations ({match.allocations.length})
          </h4>
          <ul className="space-y-1 text-sm">
            {match.allocations.map((a, i) => (
              <li
                key={`${a.objectId}-${i}`}
                className="flex justify-between rounded-md border border-surface-200 p-2"
              >
                <span>{a.displayName ?? a.objectId}</span>
                <span className="font-mono">${a.amount}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Separator />

      <section className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-surface-500">Status</span>
          <Badge
            variant={
              match.status === 'CONFIRMED'
                ? 'success'
                : match.status === 'REJECTED'
                  ? 'destructive'
                  : 'secondary'
            }
          >
            {match.status}
          </Badge>
        </div>
        <div className="flex justify-between">
          <span className="text-surface-500">Confidence</span>
          <span>{(match.matchConfidence * 100).toFixed(0)}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-surface-500">Reason</span>
          <span>{match.reason?.replace(/_/g, ' ')?.toLowerCase() ?? '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-surface-500">Created by</span>
          <span>{match.createdBy}</span>
        </div>
      </section>
    </div>
  );
}
